// src/components/RecipientSelector.jsx
import React, { useState, useEffect } from 'react';
import { getRecipients } from '../services/api';
import './RecipientSelector.css';

function RecipientSelector({ onSelect, selectedRecipient, country, excludeIds = [] }) {
  const [recipients, setRecipients] = useState([]);
  const [filteredRecipients, setFilteredRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadRecipients();
  }, []);

  useEffect(() => {
    filterRecipients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipients, searchTerm, country, excludeIds]);

  const loadRecipients = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getRecipients();
      if (response.success) {
        setRecipients(response.recipients || []);
      } else {
        setError('Failed to load recipients');
      }
    } catch (err) {
      setError(err.message || 'Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const filterRecipients = () => {
    let filtered = recipients;

    // Exclude recipients already selected in another slot of this order.
    if (excludeIds && excludeIds.length > 0) {
      filtered = filtered.filter(r => !excludeIds.includes(r.id));
    } 

    if (country) {
      filtered = filtered.filter(r => r.recipientCountry === country);
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        r =>
          r.recipientName.toLowerCase().includes(search) ||
          r.recipientPhone.includes(search) ||
          (r.nickname && r.nickname.toLowerCase().includes(search))
      );
    }

    filtered.sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      return a.recipientName.localeCompare(b.recipientName);
    });

    setFilteredRecipients(filtered);
  };

  const handleRecipientSelect = (recipient) => {
    onSelect(recipient);
    setShowDropdown(false);
    setSearchTerm('');
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowDropdown(false), 200);
  };

  const getCountryFlag = (countryCode) => {
    const flags = {
      KE: '🇰🇪',
      UG: '🇺🇬',
      TZ: '🇹🇿',
      RW: '🇷🇼',
      GH: '🇬🇭',
      NG: '🇳🇬',
      ZA: '🇿🇦',
      ET: '🇪🇹',
    };
    return flags[countryCode] || '🌍';
  };

  const getRecipientTypeIcon = (type) => {
    switch (type) {
      case 'mobile_money':
        return '📱';
      case 'bank_account':
        return '🏦';
      case 'cash_pickup':
        return '💵';
      default:
        return '👤';
    }
  };

  return (
    <div className="recipient-selector">
      <div className="selector-input-wrapper">
        <input
          type="text"
          className="selector-input"
          placeholder="Search saved recipients or enter manually..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={loading}
        />
        {loading && <span className="loading-spinner">⏳</span>}
      </div>

      {showDropdown && (
        <div className="recipient-dropdown">
          {error && (
            <div className="dropdown-error">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {!error && filteredRecipients.length === 0 && !loading && (
            <div className="dropdown-empty">
              {searchTerm ? (
                <>
                  <span>🔍</span>
                  <span>No recipients found matching "{searchTerm}"</span>
                </>
              ) : country ? (
                <>
                  <span>📋</span>
                  <span>No saved recipients yet for {getCountryFlag(country)}</span>
                </>
              ) : (
                <>
                  <span>📋</span>
                  <span>No saved recipients</span>
                </>
              )}
            </div>
          )}

          {filteredRecipients.length > 0 && (
            <div className="dropdown-list">
              {filteredRecipients.map((recipient) => (
                <div
                  key={recipient.id}
                  className={`recipient-item ${
                    selectedRecipient?.id === recipient.id ? 'selected' : ''
                  }`}
                  onClick={() => handleRecipientSelect(recipient)}
                >
                  <div className="recipient-item-main">
                    <div className="recipient-item-left">
                      <span className="recipient-type-icon">
                        {getRecipientTypeIcon(recipient.recipientType)}
                      </span>
                      <div className="recipient-info">
                        <div className="recipient-name">
                          {recipient.isFavorite && <span className="favorite-icon">⭐</span>}
                          <strong>{recipient.recipientName}</strong>
                          {recipient.nickname && (
                            <span className="nickname">"{recipient.nickname}"</span>
                          )}
                        </div>
                        <div className="recipient-details-line">
                          <span className="phone">{recipient.recipientPhone}</span>
                          <span className="separator">•</span>
                          <span className="country">
                            {getCountryFlag(recipient.recipientCountry)}
                          </span>
                          {recipient.recipientType === 'bank_account' && recipient.bankName && (
                            <>
                              <span className="separator">•</span>
                              <span className="bank">{recipient.bankName}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {recipient.transactionCount > 0 && (
                      <div className="recipient-badge">
                        {recipient.transactionCount} txns
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredRecipients.length > 0 && (
            <div className="dropdown-footer">
              {filteredRecipients.length} recipient{filteredRecipients.length !== 1 ? 's' : ''} found
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RecipientSelector;
